import { useEffect, useRef, useState } from 'react';
import { updateUserWallet } from '../../services/UsersService';

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : 'Não foi possível salvar o saldo.';
}

function WalletModal({ data, onSubmit }) {
    const closeRef = useRef(null);
    const [tradableUsdc, setTradableUsdc] = useState('');
    const [assets, setAssets] = useState([]);
    const [zeroOtherAssets, setZeroOtherAssets] = useState(false);
    const [newAsset, setNewAsset] = useState('');
    const [newQty, setNewQty] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const userId = data?.user?.id || data?.id;

    function applyData(source) {
        const wallet = Array.isArray(source?.wallet) ? source.wallet : [];
        setTradableUsdc(source?.tradableUsdc == null ? '' : String(source.tradableUsdc));
        setAssets(wallet
            .filter(row => String(row.asset || '').toUpperCase() !== 'USDC')
            .map(row => ({
                asset: String(row.asset || '').toUpperCase(),
                available: row.available == null ? '' : String(row.available)
            })));
        setZeroOtherAssets(false);
        setNewAsset('');
        setNewQty('');
        setError('');
    }

    useEffect(() => {
        applyData(data);
    }, [data?.user?.id, data?.tradableUsdc, data?.walletTotal]);

    function onAssetChange(asset, value) {
        setAssets(prev => prev.map(row => row.asset === asset ? { ...row, available: value } : row));
    }

    function onAddAsset() {
        const asset = String(newAsset || '').trim().toUpperCase();
        if (!asset || asset === 'USDC') {
            setError('Informe uma moeda diferente de USDC.');
            return;
        }
        if (assets.some(row => row.asset === asset)) {
            setError(`${asset} já está na lista.`);
            return;
        }
        setAssets(prev => [...prev, { asset, available: newQty || '0' }]);
        setNewAsset('');
        setNewQty('');
        setError('');
    }

    function onSave() {
        if (!userId) return;
        const parsed = parseFloat(tradableUsdc);
        if (!Number.isFinite(parsed) || parsed < 0) {
            setError('Informe um saldo livre em USDC maior ou igual a zero.');
            return;
        }

        const balances = zeroOtherAssets
            ? assets.map(row => ({ asset: row.asset, available: 0 }))
            : assets.map(row => ({
                asset: row.asset,
                available: row.available === '' ? 0 : parseFloat(row.available)
            }));

        setSaving(true);
        setError('');
        updateUserWallet(userId, {
            tradableUsdc: parsed,
            zeroOtherAssets,
            balances
        })
            .then(result => {
                setSaving(false);
                closeRef.current?.click();
                if (onSubmit) onSubmit(result);
            })
            .catch(err => {
                setSaving(false);
                setError(apiError(err));
            });
    }

    const nextLedger = Number.isFinite(parseFloat(tradableUsdc))
        ? parseFloat(tradableUsdc)
        : 0;

    return (
        <div className="modal fade" id="modalWallet" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">Editar saldo da carteira</p>
                        <button ref={closeRef} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small mb-3">
                            Altera só o ledger de custódia. Não mexe em Predador, monitores nem nas chaves da Binance.
                            Use Depositar para creditar USDC sem contar como lucro no PnL. Este ajuste não altera o capital depositado.
                        </p>
                        <div className="mb-3">
                            <label htmlFor="walletTradableUsdc" className="form-label">Saldo livre (USDC para comprar)</label>
                            <input
                                id="walletTradableUsdc"
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={tradableUsdc}
                                onChange={event => setTradableUsdc(event.target.value)}
                            />
                            <small className="text-muted d-block mt-1">
                                Ledger USDC ficará {nextLedger.toFixed(2)}.
                            </small>
                        </div>
                        <div className="form-check mb-3">
                            <input
                                id="walletZeroOthers"
                                className="form-check-input"
                                type="checkbox"
                                checked={zeroOtherAssets}
                                onChange={event => setZeroOtherAssets(event.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="walletZeroOthers">
                                Zerar as outras moedas
                            </label>
                        </div>
                        <div className="mb-2 fw-semibold">Outras moedas</div>
                        {!assets.length
                            ? <p className="text-muted small">Nenhuma outra moeda no ledger.</p>
                            : assets.map(row => (
                                <div className="row g-2 align-items-center mb-2" key={row.asset}>
                                    <div className="col-4">
                                        <div className="form-control-plaintext fw-bold">{row.asset}</div>
                                    </div>
                                    <div className="col-8">
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            className="form-control"
                                            disabled={zeroOtherAssets}
                                            value={zeroOtherAssets ? '0' : row.available}
                                            onChange={event => onAssetChange(row.asset, event.target.value)}
                                        />
                                    </div>
                                </div>
                            ))}
                        <div className="row g-2 align-items-end mt-2">
                            <div className="col-4">
                                <label className="form-label small mb-1">Adicionar</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="SOL"
                                    maxLength={12}
                                    value={newAsset}
                                    onChange={event => setNewAsset(event.target.value.toUpperCase())}
                                />
                            </div>
                            <div className="col-5">
                                <label className="form-label small mb-1">Quantidade</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    className="form-control"
                                    value={newQty}
                                    onChange={event => setNewQty(event.target.value)}
                                />
                            </div>
                            <div className="col-3">
                                <button type="button" className="btn btn-outline-gray-600 w-100" onClick={onAddAsset}>
                                    Incluir
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        {error
                            ? <div className="alert alert-danger mt-1 col-9 py-1 mb-0">{error}</div>
                            : <></>}
                        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={saving || !userId}>
                            {saving ? 'Salvando...' : 'Salvar saldo'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WalletModal;
